/* External */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import debounce from 'lodash-es/debounce';

/* Hooks */
import useFilterChannels from './useFilterChannels';

/* Utils */
import {
  REPORT_GA,
} from '../utils/constants';
import { gaService } from '../services/gaService';

export const SELECTED_FILTER_KEY = 'selectedFilterEpg';

export function getSavedSelectedFilter() {
  const selectedFilter = sessionStorage.getItem(SELECTED_FILTER_KEY);
  if (!selectedFilter) return undefined;

  return JSON.parse(selectedFilter);
}

function saveSelectedFilter(filterId) {
  sessionStorage.setItem(SELECTED_FILTER_KEY, JSON.stringify(filterId));
}

export default function useSelectedEpgFilter({
  filters,
  defaultFilter,
  isMiniEpgFilters = false,
  readyToFetch = true,
}) {
  const [isInitialFilter, setIsInitialFilter] = useState(true);
  const [isChangingFilter, setIsChangingFilter] = useState(false);
  const [
    selectedFilterId,
    setSelectedFilterId,
  ] = useState(readyToFetch ? defaultFilter : undefined);
  const selectedFilterIdRef = useRef(selectedFilterId);
  const selectedFilter = useMemo(() => filters.get(selectedFilterId), [filters, selectedFilterId]);

  const { channels, isFetching } = useFilterChannels(!isChangingFilter && selectedFilter);

  const debouncedFilterSelect = useRef(debounce(() => {
    setIsChangingFilter(false);
    if (!isMiniEpgFilters) {
      sessionStorage.setItem('initialSectionIndexEpg', 0);
    }
  }, 1000));

  const handleFilterSelect = useCallback((filterId) => {
    if (selectedFilterIdRef.current !== filterId) {
      setIsInitialFilter(false);
      setIsChangingFilter(true);
      setSelectedFilterId(filterId);
      debouncedFilterSelect.current();
    }
  }, []);

  useEffect(() => {
    selectedFilterIdRef.current = selectedFilterId;
    if (selectedFilterId && !isMiniEpgFilters) {
      saveSelectedFilter(selectedFilterId);
    }
  }, [selectedFilterId, isMiniEpgFilters]);

  useEffect(() => {
    if (selectedFilter) {
      gaService.sendEvent({
        label: selectedFilter.name,
        action: isMiniEpgFilters
          ? REPORT_GA.GA_EVENT_ACTION_MINI_EPG_FILTER : REPORT_GA.GA_EVENT_ACTION_EPG_FILTER,
        category: isMiniEpgFilters
          ? REPORT_GA.GA_EVENT_CATEGORY_MINI_EPG : REPORT_GA.GA_EVENT_CATEGORY_EPG,
      });
    }
  }, [selectedFilter, isMiniEpgFilters]);

  useEffect(() => () => {
    debouncedFilterSelect.current.cancel();
  }, []);

  useEffect(() => {
    if (readyToFetch && !selectedFilterIdRef.current) setSelectedFilterId(defaultFilter);
  }, [readyToFetch, defaultFilter]);

  return {
    isFetching: !selectedFilter || isFetching,
    channels,
    selectedFilter,
    handleFilterSelect,
    isInitialFilter,
  };
}
